// `.js` extension required: this package is `"type": "module"` and packages/server resolves it
// with `moduleResolution: "nodenext"`, which rejects an extensionless relative specifier. Without
// it `errorCodes` resolves to an error type on the server side, which is what made every
// `errorCodes.X` read there an implicit `any` (~25 findings across error-handler.ts,
// multipartRoute.ts and filesystem-server.service.ts). Matches the sibling convention in
// constants.ts, which already imports "./roles.js" and "./types/layout/types.js".
import { scheduleErrorCodes } from "./errorCodes/schedules.js";

export const errorCodes = {
  LICENCE_EXPIRED: 1001,
  POSITION_ALREADY_EXIST: 3001,
  POSITION_NOT_EXIST: 3002,
  ORGANISATION_NOT_EXIST: 5001,
  REGION_NOT_EXIST: 6001,
  ORGANISATION_STRUCTURE_PARENT_NOT_EXIST: 2001,
  ORGANISATION_STRUCTURE_TYPE_NOT_EXIST: 2002,
  ORGANISATION_STRUCTURE_USER_NOT_EXIST: 2003,
  ORGANISATION_STRUCTURE_REPORTS_TO_NOT_EXIST: 2004,
  ORGANISATION_STRUCTURE_NOT_EXIST: 2005,
  ORGANISATION_USERS_EXISTS : 2100,
  ORGANISATION_CONTRACT_EXISTS : 2101,
  MANAGER_REMOVE: 4001,
  ACCOUNT_NOT_FOUND: 7001,
  ACCOUNT_2FA_NOT_FOUND: 7002,
  ACCOUNT_IS_NOT_SUPERADMIN: 7003,
  ACCOUNT_2FA_UNAUTHORIZED: 7004,
  ACCOUNT_ALREADY_VERIFIED: 7005,
  ACCOUNT_ALREADY_REGISTERED: 7006,
  // Distinct from ACCOUNT_ALREADY_REGISTERED (was also 7006) so OTP flows and the
  // social-signup flow can't be confused by a handler that sees both.
  ACCOUNT_WRONG_OTP: 7034,
  ACCOUNT_OTP_EXPIRED: 7007,
  ACCOUNT_PORTAL_IS_REQUIRED: 7008,
  ACCOUNT_UNAUTHORIZED_ACTION: 7012,
  SOMETHING_WENT_WRONG: 9000,
  // Client-side transient network failure (axios ERR_NETWORK): request never
  // reached the server or no response came back. Not a server-issued code —
  // synthesized by the axios wrapper so callers see a `{ status: false }` envelope.
  NETWORK_ERROR: 9002,
  RECAPTCHA_UNUSUAL_TRAFFIC: 9001,
  ORIGIN_NOT_FOUND: 8000,
  RULE_CONTAIN_FIELD: 8800,
  RULE_UPDATE_ORGSTRUCTURE_ERROR: 8801,
  RULE_PERSONALINFORMATION_CHANGE_ERROR: 8802,
  QUERIES_DISTINCTPROPERTY_NOTFOUND: 9001,
  FILE_NOTFOUND: 9100,
  FILE_ESCROWED: 9101,
  REVISION_INVALID: 9102,
  DOCUMENT_NUMBER_AMBIGUOUS: 9103,
  // Multer/busboy aborted the request before any handler ran, because a part
  // exceeded a declared upload limit. Caller input, not a server fault, so
  // these are answered with rather than reported.
  FILE_TOO_LARGE: 9104,
  UPLOAD_FIELD_TOO_LARGE: 9105,
  // The upload is not a type the portal's ALLOWED_FILE_TYPES permits, or not
  // one the target question's own `allowedFileTypes` permits.
  FILE_TYPE_NOT_ALLOWED: 9106,
  COST_DATABASE_ITEM_DELETED_IN_LATEST_VERSION: 9200,
  COST_DATABASE_ITEM_RATE_DELETED_IN_LATEST_VERSION: 9201,
  INCORRECT_PASSWORD: 7009,
  INVITATION_EXPIRED: 7010,
  ACCOUNT_UNVERIFIED: 7011,
  INSUFFICIENT_PERMISSION: 7012,
  FAILED_TO_SAVE_AT_SPACE: 7013,
  FEW_FILES_FAILED_TO_UPLOAD: 7014,
  SPACE_NOT_FOUND: 7015,
  DELETE_DEFAULT_SPACE: 7016,
  INVALID_JSON: 7017,
  ISSUE_NOT_FOUND : 11001,
  ISSUE_NO_ACCESS : 11002,
  ISSUE_CUSTOM_FIELDS_VALIDATION_FAILED : 11003,
  ISSUE_CUSTOM_FIELDS_UPDATE_FAILED : 11004,
  // `attachedReviewId` on createIssue names a review the issue cannot join: it does not exist, or it
  // belongs to a different space. A review only ever holds resources from its own space.
  ISSUE_REVIEW_NOT_ATTACHABLE : 11005,
  ISSUE_DELETE_BLOCKED_BY_REVIEW : 11006,
  ESTIMATE_NOT_FOUND : 1101,
  ESTIMATE_NO_ACCESS : 1100,
  ESTIMATE_COST_DATABASE_UPGRADE_TAKEOFF_WIZARD_ERROR : 1201,
  ESTIMATE_MEASUREMENT_UNIT_INVALID : 1202,
  ESTIMATE_NOTHING_TO_UPDATE : 1203,
  ESTIMATE_ITEM_INVALID : 1204,
  ESTIMATE_ITEM_INCORRECT_PLACEMENT : 1205,
  DEFAULT_COST_DATABASE_DELETE : 1301,
  COST_DATABASE_ATTACHED_TO_ESTIMATE : 1302,
  ACCOUNT_SUSPENDED_IN_PORTAL: 7018,
  ADDONS_NO_ACCESS : 7019,
  ACCOUNT_PASSWORD_EXPIRED: 7020,
  ACCOUNT_PASSWORD_REUSED: 7021,
  ACCOUNT_PASSWORD_TOO_WEAK: 7022,
  ESTIMATE_GETVALIDITEMS_COST_DATABASE_NOT_EXISTS : 1102,
  COST_DATABASE_PARENT_ITEM_NOT_FOUND : 1103,
  ESTIMATE_ITEM_PURCHASE_UNIT_NOT_EDITABLE : 1110,
  TAKEOFF_NOT_FOUND : 1111,
  MEASUREMENT_TYPE_CHANGE_NOT_ALLOWED : 1112,
  MEASUREMENT_NOT_FOUND : 1113,
  SELECTION_SET_ALREADY_EXISTS : 12000,
  JIRA_TICKET_NOT_FOUND: 13001,
  JIRA_TICKET_NO_ACCESS: 13002,
  ACCOUNT_RATE_LIMIT_EXCEEDED: 7023,
  ACCOUNT_LOCKED: 7024,
  ACCOUNT_RESET_TOKEN_INVALID: 7025,
  SOCIAL_SIGNUP_UNSUPPORTED_PROVIDER: 7026,
  SOCIAL_SIGNUP_INVALID_CREDENTIAL: 7027,
  ACCOUNT_NO_PASSWORD_SOCIAL_ONLY: 7028,
  ACCOUNT_SOCIAL_ACCOUNT_NOT_LINKED: 7029,
  // The verified provider identity (sub) is already linked to a DIFFERENT account.
  SOCIAL_ACCOUNT_ALREADY_LINKED_ELSEWHERE: 7030,
  // Unlink refused: this is the user's only remaining sign-in method and no
  // password is set, so removing it would lock them out. Set a password first.
  SOCIAL_CANNOT_UNLINK_LAST_METHOD: 7031,
  // Password-change identity step: neither current password nor OTP supplied
  // for a user who has a password set.
  ACCOUNT_PASSWORD_OR_OTP_REQUIRED: 7032,
  // Password-change OTP: too many wrong attempts — temporarily locked out.
  // Response carries `lockedUntil` (epoch ms) for the retry countdown.
  ACCOUNT_OTP_LOCKED: 7033,
  ACCOUNT_NO_PASSWORD_OTP_ONLY: 7035,
  // The portal the invite was issued against no longer resolves, so there are no roles and no
  // frontendUrl to complete it with. Distinct from ACCOUNT_PORTAL_IS_REQUIRED (7008), which is a
  // missing REQUEST field rather than a missing portal document.
  ACCOUNT_INVITE_PORTAL_NOT_FOUND: 7036,
  // The invited account is already active, i.e. the invitation link has already been redeemed.
  // Distinct from INVITATION_EXPIRED (7010), which is a link that timed out without being used.
  ACCOUNT_INVITATION_ALREADY_USED: 7037,
  // 15xxx is forms. A fresh block rather than the next free 70xx: the 7xxx
  // range is crowded enough to already carry a duplicate (7012 twice), and
  // 15001 is the value forms.schema.ts's shipped description for
  // /archiveTaskInspectionAndProgress already names.
  //
  // Bulk archive/un-archive of form instances: the caller held RESOURCE_MODIFY
  // on SOME of the supplied ids, so the batch partly succeeded. Distinct from
  // INSUFFICIENT_PERMISSION (none did), and that difference is what lets the
  // client refresh the grid on a partial run but leave it alone on a full
  // denial.
  FORM_ARCHIVE_PARTIAL_PERMISSION: 15001,
  // The forms update request is internally inconsistent —
  // `addFileUploadFieldData` sent with no `file` part, without the question's
  // answer, or without a valid table row. Caller errors, so they carry a code
  // rather than being thrown as bare Errors that read as "Something went
  // wrong".
  FORM_FILE_UPLOAD_REQUEST_INVALID: 15002,
  // An `awsKey` the request refers to is not one of the files stored on that
  // question, so the caller is either racing its own delete or reaching for an
  // object it does not own.
  FORM_FILE_NOT_ON_QUESTION: 15003,
  ...scheduleErrorCodes
};
